import React, { createContext, useContext, useReducer, useEffect } from 'react';
import axios from 'axios';

const AdminAuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Initial state
const initialState = {
  admin: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Actions
const ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_ADMIN: 'SET_ADMIN',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

// Reducer
function adminAuthReducer(state, action) {
  switch (action.type) {
    case ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        admin: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        admin: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case ACTIONS.LOGOUT:
      return {
        ...state,
        admin: null,
        isAuthenticated: false,
        isLoading: false,
      };
    case ACTIONS.SET_ADMIN:
      return {
        ...state,
        admin: action.payload,
        isAuthenticated: true,
        isLoading: false,
      };
    case ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
}

// Provider
export function AdminAuthProvider({ children }) {
  const [state, dispatch] = useReducer(adminAuthReducer, initialState);

  // Check if admin is already authenticated on mount
  useEffect(() => {
    const checkAdminAuth = async () => {
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        dispatch({ type: ACTIONS.LOGOUT });
        return;
      }

      try {
        const response = await axios.get(`${BACKEND_URL}/api/admin/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        dispatch({ type: ACTIONS.SET_ADMIN, payload: response.data });
      } catch (error) {
        console.error('Admin auth check failed:', error);
        localStorage.removeItem('admin_token');
        dispatch({ type: ACTIONS.LOGOUT });
      }
    };

    checkAdminAuth();
  }, []);

  // Login
  const login = async (email, password) => {
    dispatch({ type: ACTIONS.LOGIN_START });

    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/login`, {
        email,
        password,
      });

      const { access_token } = response.data;

      // Save token
      localStorage.setItem('admin_token', access_token);

      // Get admin profile
      const profileResponse = await axios.get(`${BACKEND_URL}/api/admin/me`, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });

      dispatch({ type: ACTIONS.LOGIN_SUCCESS, payload: profileResponse.data });

      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Login failed';
      dispatch({ type: ACTIONS.LOGIN_FAILURE, payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  // Logout
  const logout = async () => {
    const token = localStorage.getItem('admin_token');

    if (token) {
      try {
        await axios.post(
          `${BACKEND_URL}/api/admin/logout`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } catch (error) {
        console.error('Logout API call failed:', error);
      }
    }

    localStorage.removeItem('admin_token');
    dispatch({ type: ACTIONS.LOGOUT });
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: ACTIONS.CLEAR_ERROR });
  };

  // Get token
  const getToken = () => {
    return localStorage.getItem('admin_token');
  };

  const value = {
    admin: state.admin,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    login,
    logout,
    clearError,
    getToken,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

// Hook
export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
}
